import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, NavLink as RouterNavLink } from 'react-router-dom';
import { User, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NavLink } from './NavLink';
import { CartBadge } from './CartBadge';
import { MobileMenu } from './MobileMenu';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { getCartCount, CART_UPDATED_EVENT } from '@/lib/cartStore';
import { useAuth } from '@/contexts/AuthContext';
import { NAVIGATION_LINKS } from '@/utils/constants';
import { useFavorites } from '@/hooks/useFavorites';

export default function NavBar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const { favorites } = useFavorites();
  const prefersReducedMotion = useReducedMotion();
  const { user, loading: authLoading } = useAuth();
  const isAuthenticated = !!user;

  // Load cart count
  useEffect(() => {
    setCartCount(getCartCount());
  }, []);

  // Listen for cart updates
  useEffect(() => {
    const handleCartUpdate = () => {
      setCartCount(getCartCount());
    };

    window.addEventListener(CART_UPDATED_EVENT, handleCartUpdate);
    return () => {
      window.removeEventListener(CART_UPDATED_EVENT, handleCartUpdate);
    };
  }, []);

  // Handle scroll for sticky shadow
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <motion.header
        className={`sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-shadow duration-300 ${
          scrolled ? 'border-b shadow-sm' : ''
        }`}
        initial={prefersReducedMotion ? { opacity: 1 } : { y: -100 }}
        animate={prefersReducedMotion ? { opacity: 1 } : { y: 0 }}
        transition={
          prefersReducedMotion
            ? { duration: 0 }
            : { duration: 0.5, ease: 'easeOut' }
        }
      >
        <div className="container mx-auto px-4">
          <div className="flex h-[72px] items-center gap-4 relative">
            {/* Mobile Menu Button - Left side */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden h-11 w-11 shrink-0 z-10"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open navigation menu"
            >
              <Menu className="h-6 w-6" />
            </Button>

            {/* Logo - Centered on mobile, normal position on desktop */}
            <Link
              to="/"
              className="flex items-center space-x-2 shrink-0 h-full absolute left-1/2 -translate-x-1/2 lg:relative lg:left-0 lg:translate-x-0"
              aria-label="Attain Domains home"
            >
              <motion.div
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                className="flex items-center h-full"
              >
                <img
                  src="/brand/logo.svg"
                  alt="Attain Domains"
                  className="h-[160px] w-auto"
                />
              </motion.div>
            </Link>

            {/* Desktop Navigation - Center (hidden on mobile) */}
            <nav
              className="hidden lg:flex items-center justify-center flex-1 gap-1 px-6"
              aria-label="Primary navigation"
            >
              {NAVIGATION_LINKS.map((link) => (
                <NavLink key={link.path} to={link.path}>
                  {link.label}
                </NavLink>
              ))}
            </nav>

            {/* Actions - Far Right (ALWAYS VISIBLE, no-wrap) */}
            <div className="flex items-center gap-4 shrink-0 ml-auto">
              {/* Cart with Badge */}
              <CartBadge itemCount={cartCount + favorites.length} />

              {/* Account Button */}
              {!authLoading &&
                (isAuthenticated ? (
                  <RouterNavLink
                    to="/account"
                    className={({ isActive }) =>
                      `relative flex items-center justify-center h-auto w-auto p-4 shrink-0 transition-colors ${
                        isActive ? 'text-[#0000FF]' : 'text-foreground hover:text-[#0000FF]'
                      }`
                    }
                    aria-label="My account"
                  >
                    {({ isActive }) => (
                      <>
                        <User className="h-7 w-7" />
                        {isActive && (
                          <motion.div
                            layoutId="activeAccountIndicator"
                            className="absolute bottom-0 left-0 right-0 h-0.5"
                            style={{ backgroundColor: '#0000FF' }}
                            transition={
                              prefersReducedMotion
                                ? { duration: 0 }
                                : { type: 'spring', stiffness: 380, damping: 30 }
                            }
                          />
                        )}
                      </>
                    )}
                  </RouterNavLink>
                ) : (
                  <RouterNavLink
                    to="/account/login"
                    className={({ isActive }) =>
                      `relative flex items-center justify-center h-auto w-auto p-4 shrink-0 transition-colors ${
                        isActive ? 'text-[#0000FF]' : 'text-foreground hover:text-[#0000FF]'
                      }`
                    }
                    aria-label="Login to your account"
                  >
                    {({ isActive }) => (
                      <>
                        <User className="h-7 w-7" />
                        {isActive && (
                          <motion.div
                            layoutId="activeAccountIndicator"
                            className="absolute bottom-0 left-0 right-0 h-0.5"
                            style={{ backgroundColor: '#0000FF' }}
                            transition={
                              prefersReducedMotion
                                ? { duration: 0 }
                                : { type: 'spring', stiffness: 380, damping: 30 }
                            }
                          />
                        )}
                      </>
                    )}
                  </RouterNavLink>
                ))}
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        navLinks={NAVIGATION_LINKS}
      />
    </>
  );
}

